import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Send, Sticker, Truck } from "lucide-react";

const EMPTY = { email: "", password: "", pickup_location: "", pickup_pincode: "" };

export const ShippingTab = () => {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [webhook, setWebhook] = useState("");

  const load = async () => {
    const [{ data: st }, { data: os }] = await Promise.all([
      api.get("/admin/shipping/settings"),
      api.get("/admin/shipping/orders"),
    ]);
    setStatus(st);
    setOrders(os);
    setForm((f) => ({ ...f, pickup_location: st.pickup_location || "", pickup_pincode: st.pickup_pincode || "" }));
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/shipping/settings", patch ?? form);
      setStatus(data);
      setForm((f) => ({ ...f, password: "" }));
      toast.success("Shipping settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/shipping/test");
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const rotateKey = async () => {
    try {
      const { data } = await api.post("/admin/shipping/webhook-key");
      setWebhook(data.webhook_key);
      toast.success("Webhook key generated — paste it into Shiprocket");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const push = async (id) => {
    try {
      await api.post(`/admin/shipping/orders/${id}/push`);
      toast.success("Order sent to Shiprocket");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const label = (id, format) => window.open(`${API}/admin/shipping/orders/${id}/label?format=${format}`, "_blank");

  if (!status) return <p className="text-sm text-muted-foreground">Loading shipping settings…</p>;

  return (
    <div className="space-y-5" data-testid="shipping-tab">
      <Card className="border-border">
        <CardContent className="p-6 lg:p-8">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-2xl flex items-center gap-2"><Truck size={18}/> Shiprocket</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                Create an API user in Shiprocket (Settings → API → Configure → Create API User) and paste
                those credentials here. Until you switch it on, nothing is sent to the courier and checkout
                is completely unaffected.
              </p>
            </div>
            <Badge
              variant="outline"
              className={status.enabled
                ? "border-primary/40 text-primary bg-primary/10"
                : "border-secondary/40 text-secondary bg-secondary/10"}
              data-testid="shipping-status-badge"
            >
              {status.enabled ? "Live" : status.has_credentials ? "Credentials saved · switched off" : "Not configured"}
            </Badge>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div>
              <Label className="overline">API user email {status.email && `(saved ${status.email})`}</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="api-user@yourdomain.com" className="mt-2 bg-transparent" data-testid="ship-email"/>
            </div>
            <div>
              <Label className="overline">API user password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="mt-2 bg-transparent" data-testid="ship-password"/>
            </div>
            <div>
              <Label className="overline">Pickup location nickname</Label>
              <Input value={form.pickup_location} onChange={(e) => setForm({ ...form, pickup_location: e.target.value })} placeholder="Primary" className="mt-2 bg-transparent" data-testid="ship-pickup"/>
            </div>
            <div>
              <Label className="overline">Pickup pincode</Label>
              <Input value={form.pickup_pincode} onChange={(e) => setForm({ ...form, pickup_pincode: e.target.value })} placeholder="411001" className="mt-2 bg-transparent" data-testid="ship-pincode"/>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-6">
            <Button onClick={() => save()} disabled={busy} className="btn-pill bg-primary text-primary-foreground h-10" data-testid="ship-save">
              {busy ? "Saving…" : "Save credentials"}
            </Button>
            <Button variant="outline" onClick={test} disabled={busy} className="btn-pill h-10" data-testid="ship-test">
              <Send size={14} className="mr-1.5"/> Test connection
            </Button>
            <Button variant="outline" onClick={rotateKey} className="btn-pill h-10" data-testid="ship-webhook-key">
              Generate webhook key
            </Button>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-muted-foreground">Integration live</span>
              <Switch
                checked={status.enabled}
                onCheckedChange={(v) => save({ enabled: v })}
                data-testid="ship-enabled-switch"
              />
            </div>
          </div>

          {webhook && (
            <div className="mt-4 rounded-lg border border-border bg-muted/30 p-3 text-xs break-all" data-testid="ship-webhook-details">
              Webhook URL: <span className="font-medium">{API}/webhooks/shiprocket</span><br/>
              x-api-key: <span className="font-medium">{webhook}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6 lg:p-8">
          <h3 className="font-display text-2xl">Labels &amp; stickers</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Every paid order gets two printable PDFs — a detailed label with the full address and a compact
            sticker — both carrying a barcode of the order ID for scanning.
          </p>

          <div className="space-y-3 mt-6">
            {orders.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="shipping-orders-empty">No paid orders yet.</p>
            )}
            {orders.map((o) => (
              <div key={o.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-4" data-testid={`ship-order-${o.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm">
                    #{o.id.slice(0, 8)} · {o.user_name}
                    {o.tracking_status && (
                      <Badge variant="outline" className="ml-2 text-[10px] border-primary/40 text-primary">{o.tracking_status}</Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate max-w-md">{o.address}</div>
                  {o.shiprocket_error && (
                    <div className="text-xs text-destructive mt-1" data-testid={`ship-error-${o.id}`}>{o.shiprocket_error}</div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" className="btn-pill" onClick={() => label(o.id, "detailed")} data-testid={`ship-label-${o.id}`}>
                    <FileText size={13} className="mr-1.5"/> Label PDF
                  </Button>
                  <Button variant="outline" size="sm" className="btn-pill" onClick={() => label(o.id, "sticker")} data-testid={`ship-sticker-${o.id}`}>
                    <Sticker size={13} className="mr-1.5"/> Sticker PDF
                  </Button>
                  {status.enabled && !o.shiprocket_shipment_id && (
                    <Button size="sm" className="btn-pill bg-primary text-primary-foreground" onClick={() => push(o.id)} data-testid={`ship-push-${o.id}`}>
                      Send to courier
                    </Button>
                  )}
                  {o.shiprocket_shipment_id && (
                    <Badge variant="outline" className="text-[10px]">Shipment {o.shiprocket_shipment_id}</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShippingTab;
